import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { documentApi } from '../services/api';
import { typedPreviewBlob, parsePptxToHtml } from '../utils/documentPreview';
import type { DocumentRecord } from '../types';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const WORD_EXTENSIONS = ['.doc', '.docx'];
const EXCEL_EXTENSIONS = ['.xls', '.xlsx'];
const PPTX_EXTENSIONS = ['.ppt', '.pptx'];

const getFileExtension = (name: string): string => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
};

/**
 * Read-only document preview shared by the document list, the provider
 * (jurist / besiktningsman) contract attachments and the contract review /
 * signing view. Same rendering pipeline as the company document tab: Word via
 * mammoth, Excel via SheetJS, PowerPoint via parsePptxToHtml, PDFs/images via
 * an object URL with a corrected MIME type.
 *
 * The modal opens immediately (loading state) and stays open on failure, so the
 * viewer can always fall back to downloading the file.
 */
export function useDocumentPreview() {
  const [previewDoc, setPreviewDoc] = useState<DocumentRecord | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [previewErrorText, setPreviewErrorText] = useState<string | null>(null);

  const openPreview = async (doc: DocumentRecord) => {
    setPreviewDoc(doc);
    setPreviewUrl(null);
    setPreviewHtml(null);
    setPreviewError(false);
    setPreviewErrorText(null);
    setPreviewLoading(true);
    await renderPreview(doc);
  };

  /**
   * Opens the preview before the document is known (e.g. the signed contract,
   * looked up first): loading under `title`, then the document, or
   * `missingText` without a download when it cannot be found.
   */
  const openPreviewFrom = async (load: () => Promise<DocumentRecord>, title: string, missingText: string) => {
    setPreviewDoc({ id: '', originalName: title } as DocumentRecord);
    setPreviewUrl(null);
    setPreviewHtml(null);
    setPreviewError(false);
    setPreviewErrorText(null);
    setPreviewLoading(true);
    let doc: DocumentRecord;
    try {
      doc = await load();
    } catch (error) {
      console.error('Preview lookup error:', error);
      setPreviewError(true);
      setPreviewErrorText(missingText);
      setPreviewLoading(false);
      return;
    }
    setPreviewDoc(doc);
    await renderPreview(doc);
  };

  const renderPreview = async (doc: DocumentRecord) => {
    try {
      const blob = await documentApi.getFileBlob(doc.id);
      const ext = getFileExtension(doc.originalName);
      if (WORD_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setPreviewHtml(result.value);
      } else if (EXCEL_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        let html = '';
        for (const name of workbook.SheetNames) {
          if (workbook.SheetNames.length > 1) html += `<h5 style="margin-top:1rem">${name}</h5>`;
          html += XLSX.utils.sheet_to_html(workbook.Sheets[name], { editable: false });
        }
        setPreviewHtml(html);
      } else if (PPTX_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        setPreviewHtml(await parsePptxToHtml(arrayBuffer));
      } else {
        setPreviewUrl(URL.createObjectURL(typedPreviewBlob(blob, doc.originalName)));
      }
    } catch (error) {
      console.error('Preview error:', error);
      setPreviewError(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewHtml(null);
    setPreviewLoading(false);
    setPreviewError(false);
    setPreviewErrorText(null);
  };

  return {
    previewDoc,
    previewUrl,
    previewHtml,
    previewLoading,
    previewError,
    previewErrorText,
    openPreview,
    openPreviewFrom,
    closePreview,
  };
}

interface DocumentPreviewModalProps {
  previewDoc: DocumentRecord | null;
  previewUrl: string | null;
  previewHtml: string | null;
  previewLoading?: boolean;
  previewError?: boolean;
  /** Replaces the generic error text (and the download) when set. */
  previewErrorText?: string | null;
  onClose: () => void;
}

export function DocumentPreviewModal({
  previewDoc,
  previewUrl,
  previewHtml,
  previewLoading = false,
  previewError = false,
  previewErrorText = null,
  onClose
}: DocumentPreviewModalProps) {
  const { t } = useTranslation();
  const open = !!previewDoc && (previewLoading || previewError || !!previewUrl || !!previewHtml);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus moves into the preview when it opens and back to where it was
  // (e.g. the row's button) when it closes.
  useEffect(() => {
    if (!open) return undefined;
    const returnTo = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>('.btn-close')?.focus();
    return () => returnTo?.focus?.();
  }, [open]);

  // Escape closes only this preview; the flow underneath (e.g. signing) stays.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!previewDoc || !open) return null;
  const ext = getFileExtension(previewDoc.originalName);
  const canRender = !!previewHtml || IMAGE_EXTENSIONS.includes(ext) || ext === '.pdf';
  const download = () => void documentApi.downloadFile(previewDoc.id, previewDoc.originalName);
  // A document that was looked up but not found has nothing to download.
  const downloadable = !!previewDoc.id;

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="document-preview-title"
      data-testid="document-preview-modal"
      onClick={(event) => {
        // Never let a click here reach an overlay underneath and close it.
        event.stopPropagation();
        onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="modal-dialog modal-lg modal-dialog-centered modal-fullscreen-sm-down"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header gap-2">
            <h5 className="modal-title text-truncate" id="document-preview-title">
              {t('documents.preview.title')} - {previewDoc.originalName}
            </h5>
            {downloadable && !previewLoading && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm document-preview-action ms-auto flex-shrink-0"
                onClick={download}
              >
                <i className="feather-download me-1" aria-hidden="true"></i>
                {t('documents.preview.download')}
              </button>
            )}
            <button
              type="button"
              className={`btn-close document-preview-action flex-shrink-0 m-0${downloadable && !previewLoading ? '' : ' ms-auto'}`}
              onClick={onClose}
              aria-label={t('documents.preview.close')}
            ></button>
          </div>
          <div className="modal-body" style={{ maxHeight: '75vh', overflow: 'auto' }}>
            {previewLoading ? (
              <div className="d-flex align-items-center justify-content-center gap-2 text-muted py-5" role="status">
                <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                {t('documents.preview.loading')}
              </div>
            ) : previewError || !canRender ? (
              <div className="text-center py-4">
                <p className="text-muted mb-3">
                  {previewError ? previewErrorText ?? t('documents.preview.error') : t('documents.preview.notSupportedDownload')}
                </p>
                {downloadable && (
                  <button type="button" className="btn btn-primary document-preview-action" onClick={download}>
                    <i className="feather-download me-1" aria-hidden="true"></i>
                    {t('documents.preview.download')}
                  </button>
                )}
              </div>
            ) : previewHtml ? (
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} style={{ textAlign: 'left' }} />
            ) : IMAGE_EXTENSIONS.includes(ext) ? (
              <div className="text-center">
                <img
                  src={previewUrl!}
                  alt={previewDoc.originalName}
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                />
              </div>
            ) : (
              <iframe
                src={previewUrl!}
                style={{ width: '100%', height: '70vh', border: 'none' }}
                title={previewDoc.originalName}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
